import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ASSETS_URL } from '../../components/Store';
import { Link } from 'react-router-dom';
import { useId } from '../../hooks/use-id';
import ReCAPTCHA from 'react-google-recaptcha';
import { useQueryParams } from '../../hooks/useQueryParams';
import MicroModal from 'micromodal';
import ModalAlertas from '../../components/ModalAlertas';
import { isDevelopment } from '../util/isDevelopment';
import { MunicipalityFormFields } from './MunicipalityFormFields';
import { useInstitutionalMunicipalitySubmit } from '../hooks/useInstitutionalMunicipalitySubmit';

/**
 * Formulario de ingreso institucional mediante municipio.
 *
 * Renderiza el formulario utilizado por instituciones que requieren
 * seleccionar un municipio y un tipo de documento antes de solicitar
 * el acceso. La configuración de las opciones se recibe mediante
 * `formConfig`, permitiendo personalizar el formulario por institución
 * sin modificar su estructura.
 *
 * También administra la validación mediante React Hook Form, el reCAPTCHA,
 * el estado de envío y la visualización de mensajes de alerta.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.datosInstituto - Información de la institución, como nombre y logo.
 * @param {Object} props.traducciones - Textos traducidos utilizados en la interfaz.
 * @param {Object} props.formConfig - Configuración específica del formulario para la institución.
 *
 * @returns {JSX.Element} FormMunicipality
 */
export const FormMunicipality = ({
    datosInstituto,
    traducciones,
    formConfig
}) => {
    const { institucional, login, accesibilidad } = traducciones;
    const { form } = institucional;
    const { nombreMostrar, logo } = datosInstituto?.data || {};

    const formController = useForm({ mode: 'all' });

    const captcha = useRef(null);
    const [captchaValido, setCaptchaValido] = useState(null);
    const captchaHelp = useId('captcha-help');

    const isDevelopmentEnvironment = isDevelopment();
    const [sendingData, setSendingData] = useState(false);
    const params = useQueryParams();
    const institucion = params.get('inst')?.replace('/', '');

    const [mostrarModal, setMostrarModal] = useState(false);

    const { submitForm } = useInstitutionalMunicipalitySubmit({
        institucion: decodeURIComponent(institucion) || '',
        onShowModal: () => {
            if (!mostrarModal) {
                setMostrarModal(true);
            }

            MicroModal.show('modal-form-institucional', {
                awaitCloseAnimation: true
            });
        }
    });

    /**
     * Valida el formulario y envía sus datos al servicio institucional.
     *
     * En ambientes diferentes a desarrollo, requiere que el reCAPTCHA
     * haya sido validado antes de permitir el envío.
     *
     * @param {Object} data - Datos validados del formulario.
     * @param {Event} e - Evento de envío del formulario.
     * @returns {Promise<void>}
     */
    const onSubmitFormHandler = async (data, e) => {
        e.preventDefault();

        if (!isDevelopmentEnvironment && captchaValido !== true) {
            console.warn('Captcha no validado');
            return;
        }

        try {
            setSendingData(true);
            await submitForm(data);
        } catch (error) {
            console.error('Error al enviar:', error);
        } finally {
            setSendingData(false);
        }
    };

    /**
     * Actualiza el estado del reCAPTCHA cuando la validación es exitosa.
     *
     * @returns {void}
     */
    const onChange = () => {
        if (captcha.current?.getValue()) {
            setCaptchaValido(true);
        }
    };

    return (
        <>
            <div className="col-lg-9 mx-auto pt-3 pb-5">
                <img
                    src={`${ASSETS_URL}${logo || '/img/logo-makemake.png'}`}
                    alt=""
                    aria-hidden={true}
                    className="mx-auto d-block"
                    style={{ maxWidth: '300px' }}
                />
                <h1
                    className="my-3 text-center pb-3 secondary-item text-24"
                    style={{ textTransform: 'capitalize' }}
                >
                    {nombreMostrar || 'Nombre del instituto'}
                </h1>

                <hr aria-hidden="true" />

                <form
                    className="row pt-3 mx-0 my-3 align-items-end"
                    name="InstitutionalFormMunicipality"
                    onSubmit={(e) => {
                        e.preventDefault();
                        formController.handleSubmit(
                            onSubmitFormHandler,
                            (errors) => {
                                console.log('🚫 NO SE PUEDE ENVIAR - Errores encontrados:');
                                Object.entries(errors).forEach(([field, error]) => {
                                    console.log(`  ❌ ${field}:`, error?.message || error?.type);
                                });
                            }
                        )(e);
                    }}
                >
                    <fieldset className="col-lg-6 mb-3 mb-lg-0">
                        <MunicipalityFormFields.InstMunicipalityField
                            formController={formController}
                            texts={traducciones}
                            options={formConfig?.fields?.municipalities?.options}
                        />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0">
                        <MunicipalityFormFields.InstIdTypeField
                            formController={formController}
                            texts={traducciones}
                            options={formConfig?.fields?.documentTypes?.options}
                        />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0">
                        <MunicipalityFormFields.InstIdField
                            formController={formController}
                            texts={traducciones}
                        />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
                        <MunicipalityFormFields.InstFullNameInput
                            formController={formController}
                            texts={traducciones}
                        />
                    </fieldset>

                    <fieldset className="col-lg-6 mb-3 mb-lg-0 mt-lg-4">
                        <MunicipalityFormFields.InstEmailInput
                            formController={formController}
                            texts={traducciones}
                        />
                    </fieldset>

                    <p className="mx-auto my-5 my-lg-0 p-2 p-lg-5 text-center mm-redimir-terms">
                        {form?.terminos?.ingresar || 'Al ingresar tus datos estás aceptando'}
                        <Link
                            className="links-tyc secondary-item focusable-red"
                            to="/terminos-y-condiciones"
                        >
                            {' '}{form?.terminos?.term_condiciones || 'Términos y condiciones'}
                        </Link>
                        {' '}{form?.terminos?.y || 'y'}{' '}
                        <Link
                            className="links-tyc secondary-item focusable-red"
                            to="/politica-de-privacidad"
                        >
                            {form?.terminos?.politica || 'Política de privacidad'}
                        </Link>
                    </p>

                    {!isDevelopmentEnvironment && (
                        <>
                            <div
                                aria-describedby={captchaHelp}
                                className="w-100 d-flex justify-content-center"
                            >
                                <ReCAPTCHA
                                    ref={captcha}
                                    sitekey="6LfaebskAAAAAF4jRB9WbC3pOOgmAaPZnOf-kn5-"
                                    onChange={onChange}
                                />
                            </div>
                            <p id={captchaHelp} className="sr-only">
                                {accesibilidad?.captcha || 'Verifica que no eres un robot'}
                            </p>
                        </>
                    )}

                    <button
                        className={`btn col-6 col-lg-4 d-block mx-auto focusable-red mt-5 ${sendingData ? 'btn-disabled' : 'btn-primary'}`}
                        type="submit"
                        disabled={sendingData}
                    >
                        {sendingData ? (
                            <>
                                <span className="sr-only">
                                    {accesibilidad?.enviando || 'Enviando formulario'}
                                </span>
                                <i className="fas fa-spinner fa-pulse" aria-hidden={true}></i>
                            </>
                        ) : (
                            login?.botones?.enviar || 'Enviar'
                        )}
                    </button>
                </form>
            </div>

            {mostrarModal && (
                <ModalAlertas
                    id="modal-form-institucional"
                    alertType={form?.modal?.titulo || 'Alerta'}
                    message={form?.modal?.mensaje || 'Error de acceso'}
                    description={
                        form?.modal?.descripcion_doc ||
                        'El nombre ingresado no coincide con el del documento'
                    }
                    boton={login?.botones?.aceptar || 'Aceptar'}
                />
            )}
        </>
    );
};